// File: src/constants/permissions.js

/**
 * Permission Constants (Frontend)
 * 
 * Mirrors backend permission constants for consistency.
 * Centralized definition of all resources, actions, and permission strings.
 */

// Resource names
export const RESOURCES = {
  USERS: 'users',
  ORGANIZATIONS: 'organizations',
  ASSESSMENTS: 'assessments',
  REPORTS: 'reports',
  SETTINGS: 'settings',
  FRAMEWORKS: 'frameworks',
  ROLES: 'roles',
  COMPLIANCE: 'compliance',
  SURVEYS: 'surveys',
  INTERVIEWS: 'interviews',
  STANDARDS: 'standards',
  CLIENTS: 'clients',
  NOTIFICATIONS: 'notifications'
};

// Action names
export const ACTIONS = {
  CREATE: 'create',
  READ: 'read',
  UPDATE: 'update',
  DELETE: 'delete',
  MANAGE: 'manage',
  EXECUTE: 'execute',
  APPROVE: 'approve',
  REVIEW: 'review',
  EXPORT: 'export',
  ASSIGN: 'assign',
  AUDIT: 'audit'
};

// Permission strings (resource.action format)
export const PERMISSIONS = {
  // All permissions
  ALL: 'all',
  
  // Users permissions
  USERS_CREATE: `${RESOURCES.USERS}.${ACTIONS.CREATE}`,
  USERS_READ: `${RESOURCES.USERS}.${ACTIONS.READ}`,
  USERS_UPDATE: `${RESOURCES.USERS}.${ACTIONS.UPDATE}`,
  USERS_DELETE: `${RESOURCES.USERS}.${ACTIONS.DELETE}`,
  USERS_MANAGE: `${RESOURCES.USERS}.${ACTIONS.MANAGE}`,
  
  // Organizations permissions
  ORGANIZATIONS_CREATE: `${RESOURCES.ORGANIZATIONS}.${ACTIONS.CREATE}`,
  ORGANIZATIONS_READ: `${RESOURCES.ORGANIZATIONS}.${ACTIONS.READ}`,
  ORGANIZATIONS_UPDATE: `${RESOURCES.ORGANIZATIONS}.${ACTIONS.UPDATE}`,
  ORGANIZATIONS_DELETE: `${RESOURCES.ORGANIZATIONS}.${ACTIONS.DELETE}`,
  ORGANIZATIONS_MANAGE: `${RESOURCES.ORGANIZATIONS}.${ACTIONS.MANAGE}`,
  
  // Assessments permissions
  ASSESSMENTS_CREATE: `${RESOURCES.ASSESSMENTS}.${ACTIONS.CREATE}`,
  ASSESSMENTS_READ: `${RESOURCES.ASSESSMENTS}.${ACTIONS.READ}`,
  ASSESSMENTS_UPDATE: `${RESOURCES.ASSESSMENTS}.${ACTIONS.UPDATE}`,
  ASSESSMENTS_DELETE: `${RESOURCES.ASSESSMENTS}.${ACTIONS.DELETE}`,
  ASSESSMENTS_MANAGE: `${RESOURCES.ASSESSMENTS}.${ACTIONS.MANAGE}`,
  ASSESSMENTS_EXECUTE: `${RESOURCES.ASSESSMENTS}.${ACTIONS.EXECUTE}`,
  ASSESSMENTS_APPROVE: `${RESOURCES.ASSESSMENTS}.${ACTIONS.APPROVE}`,
  ASSESSMENTS_REVIEW: `${RESOURCES.ASSESSMENTS}.${ACTIONS.REVIEW}`,
  
  // Reports permissions
  REPORTS_CREATE: `${RESOURCES.REPORTS}.${ACTIONS.CREATE}`,
  REPORTS_READ: `${RESOURCES.REPORTS}.${ACTIONS.READ}`,
  REPORTS_EXPORT: `${RESOURCES.REPORTS}.${ACTIONS.EXPORT}`,
  
  // Settings permissions
  SETTINGS_READ: `${RESOURCES.SETTINGS}.${ACTIONS.READ}`,
  SETTINGS_UPDATE: `${RESOURCES.SETTINGS}.${ACTIONS.UPDATE}`,
  
  // Frameworks permissions
  FRAMEWORKS_CREATE: `${RESOURCES.FRAMEWORKS}.${ACTIONS.CREATE}`,
  FRAMEWORKS_READ: `${RESOURCES.FRAMEWORKS}.${ACTIONS.READ}`,
  FRAMEWORKS_UPDATE: `${RESOURCES.FRAMEWORKS}.${ACTIONS.UPDATE}`,
  FRAMEWORKS_DELETE: `${RESOURCES.FRAMEWORKS}.${ACTIONS.DELETE}`,
  FRAMEWORKS_MANAGE: `${RESOURCES.FRAMEWORKS}.${ACTIONS.MANAGE}`,
  
  // Roles permissions
  ROLES_CREATE: `${RESOURCES.ROLES}.${ACTIONS.CREATE}`,
  ROLES_READ: `${RESOURCES.ROLES}.${ACTIONS.READ}`,
  ROLES_UPDATE: `${RESOURCES.ROLES}.${ACTIONS.UPDATE}`,
  ROLES_DELETE: `${RESOURCES.ROLES}.${ACTIONS.DELETE}`,
  ROLES_ASSIGN: `${RESOURCES.ROLES}.${ACTIONS.ASSIGN}`,
  
  // Compliance permissions
  COMPLIANCE_READ: `${RESOURCES.COMPLIANCE}.${ACTIONS.READ}`,
  COMPLIANCE_CREATE: `${RESOURCES.COMPLIANCE}.${ACTIONS.CREATE}`,
  COMPLIANCE_UPDATE: `${RESOURCES.COMPLIANCE}.${ACTIONS.UPDATE}`,
  COMPLIANCE_AUDIT: `${RESOURCES.COMPLIANCE}.${ACTIONS.AUDIT}`,
  
  // Surveys permissions
  SURVEYS_CREATE: `${RESOURCES.SURVEYS}.${ACTIONS.CREATE}`,
  SURVEYS_READ: `${RESOURCES.SURVEYS}.${ACTIONS.READ}`,
  SURVEYS_UPDATE: `${RESOURCES.SURVEYS}.${ACTIONS.UPDATE}`,
  SURVEYS_DELETE: `${RESOURCES.SURVEYS}.${ACTIONS.DELETE}`,
  SURVEYS_MANAGE: `${RESOURCES.SURVEYS}.${ACTIONS.MANAGE}`,
  
  // Interviews permissions
  INTERVIEWS_CREATE: `${RESOURCES.INTERVIEWS}.${ACTIONS.CREATE}`,
  INTERVIEWS_READ: `${RESOURCES.INTERVIEWS}.${ACTIONS.READ}`,
  INTERVIEWS_UPDATE: `${RESOURCES.INTERVIEWS}.${ACTIONS.UPDATE}`,
  INTERVIEWS_DELETE: `${RESOURCES.INTERVIEWS}.${ACTIONS.DELETE}`,
  INTERVIEWS_MANAGE: `${RESOURCES.INTERVIEWS}.${ACTIONS.MANAGE}`,
  
  // Standards permissions
  STANDARDS_CREATE: `${RESOURCES.STANDARDS}.${ACTIONS.CREATE}`,
  STANDARDS_READ: `${RESOURCES.STANDARDS}.${ACTIONS.READ}`,
  STANDARDS_UPDATE: `${RESOURCES.STANDARDS}.${ACTIONS.UPDATE}`,
  STANDARDS_DELETE: `${RESOURCES.STANDARDS}.${ACTIONS.DELETE}`,
  STANDARDS_MANAGE: `${RESOURCES.STANDARDS}.${ACTIONS.MANAGE}`
};

/**
 * Helper function to construct permission string
 * @param {string} resource - Resource name
 * @param {string} action - Action name
 * @returns {string} Permission string in format "resource.action"
 */
export function buildPermission(resource, action) {
  return `${resource}.${action}`;
}

/**
 * Helper function to parse permission string
 * @param {string} permission - Permission string in format "resource.action"
 * @returns {Object} Object with resource and action properties
 */
export function parsePermission(permission) {
  const parts = permission.split('.');
  if (parts.length !== 2) {
    throw new Error(`Invalid permission format: ${permission}`);
  }
  return {
    resource: parts[0],
    action: parts[1]
  };
}

/**
 * Check if a permission string is valid
 * @param {string} permission - Permission string to validate
 * @returns {boolean} True if valid
 */
export function isValidPermission(permission) {
  if (permission === 'all') {
    return true;
  }
  const parts = permission.split('.');
  return parts.length === 2 && 
         Object.values(RESOURCES).includes(parts[0]) &&
         Object.values(ACTIONS).includes(parts[1]);
}

export default {
  RESOURCES,
  ACTIONS,
  PERMISSIONS,
  buildPermission,
  parsePermission,
  isValidPermission
};

